/**
 * 未付款清單 Payables-Outstanding report (S11.1).
 *
 * Vendor (AP) invoices not yet paid (應付), with a due/overdue aging flag
 * (到期/逾期) against the due date. Per-currency payable totals only. AP
 * invoices carry no partial-payment field, so the full amount is the payable.
 */

import { useMemo } from 'react';
import { Card, Col, Row, Statistic, Table, Tag, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import { formatAmount, formatMoney } from '@/components/salesOrders/components/soFormat';
import type { ApInvoice, ApInvoiceStatus } from '@/types/api/finance';
import { useReportApInvoices } from '../reportsData';
import { ReportSyncNote, ReportToolbar } from '../reportsShared';
import { exportReportCsv } from '../reportsCsv';

const { Text } = Typography;

interface ApRow extends ApInvoice {
  daysOverdue: number;
}

const ApOutstandingReport = () => {
  const { t } = useTranslation(['reports', 'finance', 'common']);
  const { token } = theme.useToken();
  const { rows: invoices, isLoading, error, refresh } = useReportApInvoices();

  const { rows, generatedAt } = useMemo(() => {
    const today = dayjs().startOf('day');
    const derived: ApRow[] = invoices
      .filter((invoice) => invoice.status !== 'paid')
      .map((invoice) => ({
        ...invoice,
        daysOverdue: today.diff(dayjs(invoice.dueDate).startOf('day'), 'day'),
      }))
      .sort((a, b) => b.daysOverdue - a.daysOverdue);
    return { rows: derived, generatedAt: new Date().toISOString() };
  }, [invoices]);

  const totals = useMemo(() => {
    const map = new Map<string, number>();
    rows.forEach((row) => map.set(row.currency, (map.get(row.currency) ?? 0) + row.amount));
    return [...map.entries()].map(([currency, amount]) => ({ currency, amount }));
  }, [rows]);
  const overdueCount = useMemo(() => rows.filter((row) => row.daysOverdue > 0).length, [rows]);

  const agingTag = (daysOverdue: number) =>
    daysOverdue > 0 ? (
      <Tag color="red">{t('reports:reports.aging.overdue', { days: daysOverdue })}</Tag>
    ) : (
      <Tag color="default">{t('reports:reports.aging.current', { days: -daysOverdue })}</Tag>
    );

  const columns: TableProps<ApRow>['columns'] = [
    {
      title: t('reports:reports.payables.columns.invoice'),
      key: 'invoice',
      width: 160,
      fixed: 'left',
      render: (_: unknown, row) => (
        <span style={{ fontWeight: token.fontWeightStrong }}>{row.invoiceNumber}</span>
      ),
    },
    {
      title: t('reports:reports.payables.columns.vendor'),
      dataIndex: 'vendorName',
      key: 'vendorName',
      width: 220,
      render: (value: string) => renderDash(value),
    },
    {
      title: t('reports:reports.payables.columns.po'),
      dataIndex: 'poNumber',
      key: 'poNumber',
      width: 150,
      render: (value: string | undefined) => renderDash(value),
    },
    {
      title: t('reports:reports.payables.columns.amount'),
      key: 'amount',
      width: 150,
      align: 'right',
      render: (_: unknown, row) => (
        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: token.fontWeightStrong }}>
          {formatMoney(row.currency, row.amount)}
        </span>
      ),
    },
    {
      title: t('reports:reports.payables.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (status: ApInvoiceStatus) => (
        <Tag>{t(`finance:invoices.statusVendor.${status}`)}</Tag>
      ),
    },
    {
      title: t('reports:reports.payables.columns.dueDate'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 120,
      render: (value: string) => renderDash(value),
    },
    {
      title: t('reports:reports.payables.columns.aging'),
      key: 'aging',
      width: 130,
      render: (_: unknown, row) => agingTag(row.daysOverdue),
    },
  ];

  const handleExport = () => {
    exportReportCsv<ApRow>(
      'payables-outstanding.csv',
      [
        { header: t('reports:reports.payables.columns.invoice'), value: (row) => row.invoiceNumber },
        { header: t('reports:reports.payables.columns.vendor'), value: (row) => row.vendorName },
        { header: t('reports:reports.payables.columns.po'), value: (row) => row.poNumber ?? '' },
        { header: t('reports:reports.common.currency'), value: (row) => row.currency },
        { header: t('reports:reports.payables.columns.amount'), value: (row) => formatAmount(row.amount) },
        { header: t('reports:reports.payables.columns.status'), value: (row) => t(`finance:invoices.statusVendor.${row.status}`) },
        { header: t('reports:reports.payables.columns.dueDate'), value: (row) => row.dueDate },
        { header: t('reports:reports.payables.columns.aging'), value: (row) => (row.daysOverdue > 0 ? t('reports:reports.aging.overdue', { days: row.daysOverdue }) : t('reports:reports.aging.current', { days: -row.daysOverdue })) },
      ],
      rows
    );
  };

  return (
    <>
      <ReportToolbar
        formula={t('reports:reports.payables.formula')}
        onExport={handleExport}
        exportDisabled={rows.length === 0}
      />

      <Row gutter={[12, 12]} style={{ marginBottom: token.marginLG }}>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic
              title={t('reports:reports.payables.stats.openInvoices')}
              value={rows.length}
            />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic
              title={t('reports:reports.payables.stats.overdue')}
              value={overdueCount}
              valueStyle={{ color: overdueCount > 0 ? token.colorError : undefined }}
            />
          </Card>
        </Col>
        {totals.map((entry) => (
          <Col xs={12} lg={6} key={entry.currency}>
            <Card size="small">
              <Statistic
                title={t('reports:reports.payables.stats.payable', { currency: entry.currency })}
                value={formatAmount(entry.amount)}
              />
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {entry.currency}
              </Text>
            </Card>
          </Col>
        ))}
      </Row>

      <TableContainer
        errorState={{
          active: Boolean(error),
          title: t('reports:reports.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<ApRow>
          rowKey="id"
          dataSource={rows}
          columns={columns}
          loading={isLoading}
          locale={{ emptyText: <ListEmptyState title={t('reports:reports.payables.empty')} /> }}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: 1140 }}
        />
      </TableContainer>
      <ReportSyncNote syncedAt={generatedAt} />
    </>
  );
};

export default ApOutstandingReport;
